/**
 * §10 research wire types — Phase 2.F.1.
 *
 * The research catalog itself lives in `@stahlfront/core` (server-authoritative).
 * Shared schemas here cover the over-the-wire shapes and the request/response
 * contracts; tick-worker, server, and client all import from here.
 */
import { z } from "zod";

export const ResearchStateSchema = z.enum(["active", "complete"]);
export type ResearchState = z.infer<typeof ResearchStateSchema>;

/** Active or recently-finished research timer. */
export const ResearchJobDtoSchema = z.object({
  id: z.string().uuid(),
  playerId: z.string().uuid(),
  nodeId: z.string().min(1),
  startedAtMs: z.number().int(),
  durationMs: z.number().int().positive(),
  state: ResearchStateSchema,
});
export type ResearchJobDto = z.infer<typeof ResearchJobDtoSchema>;

/* ── POST /api/research/start ───────────────────────────────────────────── */

export const StartResearchRequestSchema = z.object({
  nodeId: z.string().min(1),
});
export type StartResearchRequest = z.infer<typeof StartResearchRequestSchema>;

export const StartResearchResponseSchema = z.object({
  ok: z.literal(true),
  job: ResearchJobDtoSchema,
  /** Refreshed wallet so the client can paint the cost drain without a follow-up GET. */
  wallet: z.object({
    playerId: z.string().uuid(),
    stockpile: z.object({
      PROVISIONS: z.number(),
      TREASURY: z.number(),
      RESERVES: z.number(),
    }),
    industrial: z.record(z.string(), z.number()),
    diamonds: z.number(),
  }),
});
export type StartResearchResponse = z.infer<typeof StartResearchResponseSchema>;

/* ── POST /api/research/speedup ─────────────────────────────────────────── */

export const SpeedupResearchRequestSchema = z.object({
  jobId: z.string().uuid(),
});
export type SpeedupResearchRequest = z.infer<typeof SpeedupResearchRequestSchema>;

export const SpeedupResearchResponseSchema = z.object({
  ok: z.literal(true),
  job: ResearchJobDtoSchema,
  wallet: z.object({
    playerId: z.string().uuid(),
    stockpile: z.object({
      PROVISIONS: z.number(),
      TREASURY: z.number(),
      RESERVES: z.number(),
    }),
    industrial: z.record(z.string(), z.number()),
    diamonds: z.number(),
  }),
  costDiamonds: z.number().int().nonnegative(),
});
export type SpeedupResearchResponse = z.infer<typeof SpeedupResearchResponseSchema>;

/* ── GET /api/research/list ─────────────────────────────────────────────── */

export const ResearchListResponseSchema = z.object({
  serverNowMs: z.number().int(),
  /** All active jobs for the calling player (max = researchSlotCount(researchCenterLevel)). */
  activeJobs: z.array(ResearchJobDtoSchema),
  /** Stable list of completed node ids (de-duplicated). */
  completedNodeIds: z.array(z.string()),
  /** Slot budget so the client can render `X / Y` headers without recomputing. */
  slots: z.object({
    used: z.number().int().nonnegative(),
    max: z.number().int().nonnegative(),
  }),
});
export type ResearchListResponse = z.infer<typeof ResearchListResponseSchema>;
